<template>
  <section class="page-section">
    <div class="section-head">
      <h2 class="section-title">{{ pageTitle }}</h2>
      <div v-if="userStore.isEnterprise" class="quick-actions">
        <el-button type="primary" @click="router.push({ path: '/reports/new', query: { mode: 'word' } })">
          上传 Word 周报
        </el-button>
        <el-button @click="router.push('/reports/new')">在线新建</el-button>
      </div>
    </div>

    <div v-if="userStore.isEnterprise && profile" class="profile-shell">
      <div class="profile-toolbar">
        <div class="profile-identity">
          <el-avatar :src="editing ? profileForm.logo : profile.logo" :size="72">
            {{ buildAvatarText(editing ? profileForm.name : profile.name) }}
          </el-avatar>
          <div class="profile-copy">
            <div class="profile-name">{{ editing ? profileForm.name || '未命名企业' : profile.name }}</div>
            <div class="muted">账号：{{ profile.code }}</div>
          </div>
        </div>

        <div class="profile-actions">
          <template v-if="editing">
            <el-button :disabled="savingProfile" @click="cancelEdit">取消</el-button>
            <el-button type="primary" :loading="savingProfile" @click="handleSaveProfile">保存信息</el-button>
          </template>
          <el-button v-else @click="startEdit">编辑信息</el-button>
        </div>
      </div>

      <el-form
        v-if="editing"
        ref="formRef"
        :model="profileForm"
        :rules="rules"
        label-width="92px"
        class="profile-form"
      >
        <el-form-item label="企业名称" prop="name">
          <el-input v-model="profileForm.name" maxlength="80" show-word-limit placeholder="输入企业名称" />
        </el-form-item>

        <el-form-item label="企业账号">
          <el-input :model-value="profileForm.code" disabled />
        </el-form-item>

        <el-form-item label="联系人" prop="contact">
          <el-input
            v-model="profileForm.contact"
            maxlength="40"
            show-word-limit
            clearable
            placeholder="输入联系人"
          />
        </el-form-item>

        <el-form-item label="联系电话" prop="phone">
          <el-input
            v-model="profileForm.phone"
            maxlength="30"
            show-word-limit
            clearable
            placeholder="输入联系电话"
          />
        </el-form-item>

        <el-form-item label="企业 Logo" prop="logo">
          <div class="logo-editor">
            <el-avatar :src="profileForm.logo" :size="72">{{ buildAvatarText(profileForm.name) }}</el-avatar>
            <div class="logo-actions">
              <div class="logo-button-row">
                <el-upload
                  :show-file-list="false"
                  accept=".jpg,.jpeg,.png,.gif,.webp,.bmp"
                  :http-request="handleLogoUpload"
                  :before-upload="beforeLogoUpload"
                  :disabled="uploadingLogo"
                >
                  <el-button :loading="uploadingLogo">上传 Logo</el-button>
                </el-upload>
                <el-button v-if="profileForm.logo" text @click="profileForm.logo = ''">清空</el-button>
              </div>
              <el-input
                v-model="profileForm.logo"
                maxlength="500"
                show-word-limit
                clearable
                placeholder="也可以直接粘贴图片地址"
              />
              <div class="muted">支持 jpg、png、gif、webp、bmp，建议使用方形图片。</div>
            </div>
          </div>
        </el-form-item>

        <el-form-item label="状态">
          <el-tag :type="profile.status === 1 ? 'success' : 'danger'">
            {{ profile.status === 1 ? '启用' : '停用' }}
          </el-tag>
        </el-form-item>
      </el-form>

      <el-descriptions v-else :column="1" border>
        <el-descriptions-item label="企业名称">{{ profile.name }}</el-descriptions-item>
        <el-descriptions-item label="企业账号">{{ profile.code }}</el-descriptions-item>
        <el-descriptions-item label="联系人">{{ profile.contact || '' }}</el-descriptions-item>
        <el-descriptions-item label="联系电话">{{ profile.phone || '' }}</el-descriptions-item>
        <el-descriptions-item label="状态">
          <el-tag :type="profile.status === 1 ? 'success' : 'danger'">
            {{ profile.status === 1 ? '启用' : '停用' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="Logo">
          <a v-if="profile.logo" :href="profile.logo" target="_blank" rel="noreferrer">{{ profile.logo }}</a>
          <span v-else>-</span>
        </el-descriptions-item>
      </el-descriptions>
    </div>

    <div v-else-if="userStore.isEnterprise" class="muted">暂无企业资料</div>

    <el-table v-else :data="enterpriseList" v-loading="loading">
      <el-table-column label="企业" min-width="260">
        <template #default="{ row }">
          <div class="enterprise-cell">
            <el-avatar :src="row.logo" :size="38">{{ buildAvatarText(row.name) }}</el-avatar>
            <div class="enterprise-copy">
              <div class="enterprise-name">{{ row.name }}</div>
              <div class="muted">{{ row.code }}</div>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="contact" label="联系人" width="120" />
      <el-table-column prop="phone" label="联系电话" width="150" />
      <el-table-column prop="total_reports" label="总周报数" width="110" />
      <el-table-column prop="approved_reports" label="已发布" width="110" />
      <el-table-column prop="latest_report_at" label="最新更新时间" width="180" />
      <el-table-column label="状态" width="100">
        <template #default="{ row }">
          <el-tag :type="row.status === 1 ? 'success' : 'danger'">
            {{ row.status === 1 ? '启用' : '停用' }}
          </el-tag>
        </template>
      </el-table-column>
    </el-table>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { ElMessage } from 'element-plus';
import { useRouter } from 'vue-router';

import { getAdminEnterpriseList, getMyEnterpriseProfile, updateMyEnterpriseProfile } from '../../api/enterprise';
import { uploadSingleFile } from '../../api/upload';
import { useUserStore } from '../../store/user';

const MAX_LOGO_FILE_SIZE = 5 * 1024 * 1024;
const IMAGE_EXTENSION_RE = /\.(jpg|jpeg|png|gif|webp|bmp)$/i;
const PHONE_RE = /^[0-9+\-() ]*$/;

const router = useRouter();
const userStore = useUserStore();
const formRef = ref();
const loading = ref(false);
const savingProfile = ref(false);
const uploadingLogo = ref(false);
const editing = ref(false);
const profile = ref(null);
const enterpriseList = ref([]);

const profileForm = reactive({
  name: '',
  code: '',
  contact: '',
  phone: '',
  logo: ''
});

const pageTitle = computed(() => (userStore.isAdmin ? '企业目录' : '企业信息'));

const rules = {
  name: [{ required: true, message: '请输入企业名称', trigger: 'blur' }],
  contact: [
    {
      validator: (rule, value, callback) => {
        if (String(value || '').trim().length > 40) {
          callback(new Error('联系人不能超过 40 个字符'));
          return;
        }
        callback();
      },
      trigger: 'blur'
    }
  ],
  phone: [
    {
      validator: (rule, value, callback) => {
        const text = String(value || '').trim();
        if (!text) {
          callback();
          return;
        }
        if (text.length > 30) {
          callback(new Error('联系电话不能超过 30 个字符'));
          return;
        }
        if (!PHONE_RE.test(text)) {
          callback(new Error('联系电话格式不正确'));
          return;
        }
        callback();
      },
      trigger: 'blur'
    }
  ],
  logo: [
    {
      validator: (rule, value, callback) => {
        if (String(value || '').trim().length > 500) {
          callback(new Error('Logo 地址不能超过 500 个字符'));
          return;
        }
        callback();
      },
      trigger: 'blur'
    }
  ]
};

const syncProfileForm = (data) => {
  profileForm.name = data?.name || '';
  profileForm.code = data?.code || '';
  profileForm.contact = data?.contact || '';
  profileForm.phone = data?.phone || '';
  profileForm.logo = data?.logo || '';
};

const buildAvatarText = (value) => String(value || '企').trim().slice(0, 1) || '企';

const startEdit = () => {
  syncProfileForm(profile.value);
  editing.value = true;
};

const cancelEdit = () => {
  syncProfileForm(profile.value);
  editing.value = false;
};

const loadData = async () => {
  loading.value = true;
  try {
    if (userStore.isAdmin) {
      const result = await getAdminEnterpriseList();
      enterpriseList.value = result.data || [];
      return;
    }

    const result = await getMyEnterpriseProfile();
    profile.value = result.data;
    syncProfileForm(result.data);
  } catch (error) {
    ElMessage.error(error.response?.data?.message || '获取企业资料失败');
  } finally {
    loading.value = false;
  }
};

const beforeLogoUpload = (file) => {
  const fileName = String(file?.name || '');
  const isImage = IMAGE_EXTENSION_RE.test(fileName);
  if (!isImage) {
    ElMessage.warning('请上传 jpg、png、gif、webp 或 bmp 图片');
    return false;
  }

  if ((Number(file?.size) || 0) > MAX_LOGO_FILE_SIZE) {
    ElMessage.warning('Logo 图片不能超过 5MB');
    return false;
  }

  return true;
};

const handleLogoUpload = async ({ file, onSuccess, onError }) => {
  uploadingLogo.value = true;
  try {
    const result = await uploadSingleFile(file, 'image');
    profileForm.logo = result.data?.file_url || '';
    ElMessage.success('Logo 上传成功');
    onSuccess?.(result.data);
  } catch (error) {
    ElMessage.error(error.response?.data?.message || 'Logo 上传失败');
    onError?.(error);
  } finally {
    uploadingLogo.value = false;
  }
};

const handleSaveProfile = async () => {
  try {
    await formRef.value?.validate();
  } catch (error) {
    return;
  }

  savingProfile.value = true;
  try {
    const payload = {
      name: profileForm.name,
      contact: profileForm.contact,
      phone: profileForm.phone,
      logo: profileForm.logo
    };

    const result = await updateMyEnterpriseProfile(payload);
    profile.value = result.data;
    syncProfileForm(result.data);
    userStore.setProfile({
      ...(userStore.profile || {}),
      ...result.data,
      type: 'enterprise'
    });
    editing.value = false;
    ElMessage.success(result.message || '企业资料已保存');
  } catch (error) {
    ElMessage.error(error.response?.data?.message || '企业资料保存失败');
  } finally {
    savingProfile.value = false;
  }
};

onMounted(loadData);
</script>

<style scoped>
.section-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.quick-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.profile-shell {
  display: grid;
  gap: 20px;
}

.profile-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.profile-identity {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.profile-copy {
  min-width: 0;
}

.profile-name {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.4;
  word-break: break-word;
}

.profile-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.profile-form {
  max-width: 760px;
}

.logo-editor {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
}

.logo-actions {
  flex: 1 1 auto;
  display: grid;
  gap: 10px;
  min-width: 0;
}

.logo-button-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.enterprise-cell {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.enterprise-copy {
  min-width: 0;
}

.enterprise-name {
  font-weight: 600;
  word-break: break-word;
}

@media (max-width: 768px) {
  .logo-editor {
    flex-direction: column;
  }
}
</style>
